import api from '../lib/axios';
import type { Product } from '../types/product';
import { logger } from '../utils/logger';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  PRODUCTS: import.meta.env.VITE_API_V1_PRODUCTS,
  PRODUCTS_CATEGORY: import.meta.env.VITE_API_V1_PRODUCTS_CATEGORY,
};

export interface ProductResponse {
  success: boolean;
  message: string;
  data?: Product[];
  meta?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

export interface ProductsParams {
  page?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  language?: string;
}

export interface Pagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

export const getProductsByCategory = async (
  categoryId: string,
  params: ProductsParams = {},
  userLanguage?: string,
): Promise<{ products: Product[]; pagination: Pagination }> => {
  try {
    const { page = 1, limit = 10, sortField = 'price', sortOrder = 'asc', language } = params;

    // Use userLanguage parameter first, then params.language, then default to 'en'
    const requestLanguage = userLanguage || language || 'en';

    const response = await api.get<ProductResponse>(
      `${API_ENDPOINTS.PRODUCTS_CATEGORY}/${categoryId}`,
      {
        params: {
          page,
          limit,
          sortField,
          sortOrder,
        },
        headers: {
          'Accept-Language': requestLanguage,
        },
      },
    );

    if (response.data.success && response.data.data) {
      return {
        products: response.data.data || [],
        pagination: {
          currentPage: response.data.meta?.page || 1,
          totalPages: response.data.meta?.totalPages || 1,
          totalItems: response.data.meta?.total || 0,
          itemsPerPage: response.data.meta?.limit || 10,
        },
      };
    }

    return {
      products: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      },
    };
  } catch (error) {
    logger.error(`Error fetching products for category ${categoryId}:`, error);
    return {
      products: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
      },
    };
  }
};

export const getAllProducts = async (
  params: ProductsParams = {},
  userLanguage?: string,
): Promise<Product[]> => {
  try {
    const { page = 1, limit = 50, sortField = 'price', sortOrder = 'asc', language } = params;

    // Use userLanguage parameter first, then params.language, then default to 'en'
    const requestLanguage = userLanguage || language || 'en';

    const response = await api.get<ProductResponse>(API_ENDPOINTS.PRODUCTS, {
      params: {
        page,
        limit,
        sortField,
        sortOrder,
      },
      headers: {
        'Accept-Language': requestLanguage,
      },
    });

    if (response.data.success && response.data.data) {
      return response.data.data || [];
    }

    return [];
  } catch (error) {
    logger.error('Error fetching all products:', error);
    return [];
  }
};

export const getProductById = async (
  productId: string,
  userLanguage?: string,
): Promise<Product | null> => {
  try {
    const requestLanguage = userLanguage || 'en';

    const response = await api.get<{ success: boolean; data: Product }>(
      `/api/v1/products/${productId}`,
      {
        headers: {
          'Accept-Language': requestLanguage,
        },
      },
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    return null;
  } catch (error) {
    logger.error(`Error fetching product ${productId}:`, error);
    return null;
  }
};
